<script setup lang="ts">
import { onMounted } from 'vue'
import { RouterView } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import AppToast from '@/components/common/AppToast.vue'
import AppModal from '@/components/common/AppModal.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppSpinner from '@/components/common/AppSpinner.vue'
import { setTokenGetter, setAuthErrorHandler, setApiErrorHandler } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

const { isLoading, error, logout } = useAuth0()
const authStore = useAuthStore()
const uiStore = useUiStore()

onMounted(() => {
  setTokenGetter(authStore.getToken)
  setAuthErrorHandler(() => {
    logout({ logoutParams: { returnTo: window.location.origin } })
  })
  setApiErrorHandler((title, message) => {
    uiStore.showApiError(title, message)
  })
})

function handleLogout() {
  logout({ logoutParams: { returnTo: window.location.origin } })
}
</script>

<template>
  <!-- Auth0 loading -->
  <div v-if="isLoading" class="min-h-screen bg-sf-bg-primary flex flex-col items-center justify-center gap-4">
    <AppSpinner size="lg" />
    <p class="text-sm text-sf-text-secondary">Loading...</p>
  </div>

  <!-- Auth0 error (expired token, forbidden, etc.) -->
  <div v-else-if="error" class="min-h-screen bg-sf-bg-primary flex flex-col items-center justify-center gap-6">
    <div class="bg-sf-bg-card border border-sf-border rounded-[16px] p-8 max-w-md text-center">
      <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 text-sf-danger mx-auto mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <h1 class="text-lg font-semibold text-sf-text-primary mb-2">Authentication Error</h1>
      <p class="text-sm text-sf-text-secondary mb-6">{{ error.message }}</p>
      <div class="flex gap-3 justify-center">
        <button
          class="px-4 py-2 text-sm font-medium rounded-[12px] bg-sf-accent text-white hover:bg-sf-accent-hover transition-colors cursor-pointer border-none"
          @click="handleLogout"
        >
          Sign in again
        </button>
      </div>
    </div>
  </div>

  <!-- App ready -->
  <template v-else>
    <RouterView />
    <AppToast />

    <!-- Global API error modal -->
    <AppModal
      :show="!!uiStore.apiError"
      :title="uiStore.apiError?.title ?? 'Error'"
      size="sm"
      @close="uiStore.dismissApiError()"
    >
      <div class="flex flex-col items-center text-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-sf-danger mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p class="text-sm text-sf-text-secondary leading-relaxed">
          {{ uiStore.apiError?.message }}
        </p>
      </div>
      <template #footer>
        <AppButton @click="uiStore.dismissApiError()">Dismiss</AppButton>
      </template>
    </AppModal>
  </template>
</template>
